import { BOOKING_LIST_ACTIONS, REQUEST_ACTIONS } from '../Constants/Actions.constants';
import { CORE_SERVICE_URLS } from '../Constants/Urls.constants';
import {
  IBookingCreateRequest,
  IBookingListRequest,
  IBookingUpdate,
  IPresenceUpdate,
  IBookingSummaryRequest,
  BookingStatus,
} from '../Models/DTO/Booking.models';
import { ICreatePaymentRequest } from '../Models/interfaces/Payments.models';
import { createAction, createRequestAction } from '../Helpers/Action.helpers';
import { createQueriedURL, routeReplacer } from '../Helpers/String.helper';
import { CustomerBookingType, FullCustomer, ICreateCustomer } from '../Models/DTO/Customer.model';
import { ICreateOrderline, IOrderline } from '../Models/DTO/Orderline.models';

export const requestExportCustomerList = (
  domain: string,
  customersIds: number[] = [],
  tagIds: string[] = [],
) =>
  createRequestAction({
    name: REQUEST_ACTIONS.EXPORT_CUSTOMERS,
    url: routeReplacer(CORE_SERVICE_URLS.EXPORT_CUSTOMERS, { domain }),
    method: 'POST',
    data: {
      customersIds,
      tagIds,
      allCustomers: customersIds.length === 0,
    },
  });

export interface IGetCustomerListRequest {
  domain: string;
  page?: number;
  pageSize?: number;
}

export const requestCustomerList = (request: IGetCustomerListRequest) =>
  createRequestAction({
    name: REQUEST_ACTIONS.CUSTOMER_LIST,
    url: createQueriedURL(
      routeReplacer(CORE_SERVICE_URLS.CUSTOMER_LIST, { domain: request.domain }),
      { page: 1, pageSize: 30, ...request },
    ),
    method: 'GET',
    request,
  });

export const requestCustomerById = (id: string) =>
  createRequestAction({
    name: REQUEST_ACTIONS.SINGLE_CUSTOMER,
    url: routeReplacer(CORE_SERVICE_URLS.SINGLE_CUSTOMER, { id }),
    method: 'GET',
  });

export const requestCreateCustomer = (newCustomer: ICreateCustomer) =>
  createRequestAction({
    url: CORE_SERVICE_URLS.CREATE_CUSTOMER,
    method: 'POST',
    name: REQUEST_ACTIONS.CREATE_CUSTOMER,
    data: newCustomer,
  });

export const requestUpdateCustomer = (customerUpdate: FullCustomer, providerId: string) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.UPDATE_CUSTOMER, { providerId, id: customerUpdate.id }),
    method: 'PATCH',
    name: REQUEST_ACTIONS.UPDATE_CUSTOMER,
    data: customerUpdate,
  });

export const requestInviteCustomer = (providerId: string, id: string) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.INVITE_CUSTOMER, { providerId, id }),
    method: 'POST',
    name: REQUEST_ACTIONS.INVITE_CUSTOMER,
  });

export const requestDisconnectCustomer = (id: string) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.DISCONNECT_CUSTOMER, { id }),
    method: 'POST',
    name: REQUEST_ACTIONS.DISCONNECT_CUSTOMER,
  });

export const requestUpdateBookingPresence = ({
  bookingId,
  reason,
  presenceType,
  origin,
}: IPresenceUpdate) =>
  createRequestAction({
    name: REQUEST_ACTIONS.BOOKING_PRESENCE_UPDATE,
    method: 'POST',
    url: CORE_SERVICE_URLS.BOOKING_PRESENCE_UPDATE,
    data: {
      bookingId,
      presenceType,
      reason,
      origin,
    },
  });

export const requestUpdateMultipleBookingPresence = (update: IPresenceUpdate[]) =>
  createRequestAction({
    name: REQUEST_ACTIONS.BOOKING_UPDATE_PRESENCE_MULTIPLE,
    method: 'POST',
    url: CORE_SERVICE_URLS.BOOKING_PRESENCE_UPDATE_MULTIPLE,
    data: update,
  });

export const requestBookingListForEvent = (request: IBookingListRequest) => {
  const url = createQueriedURL(
    routeReplacer(CORE_SERVICE_URLS.BOOKING_LIST, { providerId: request.domain }),
    request,
  );

  return createRequestAction({
    name: REQUEST_ACTIONS.BOOKING_LIST,
    method: 'GET',
    url,
  });
};

export const requestBookingSummaryList = (request: IBookingSummaryRequest) =>
  createRequestAction({
    name: REQUEST_ACTIONS.BOOKING_SUMMARY,
    method: 'POST',
    url: CORE_SERVICE_URLS.BOOKING_SUMMARY,
    data: request,
  });

export const requestChangeCheckinStatus = (status: boolean, eventId: string) =>
  createAction({
    name: BOOKING_LIST_ACTIONS.CHECKIN_STATUS,
    type: BOOKING_LIST_ACTIONS.CHECKIN_STATUS,
    payload: {
      status,
      eventId,
    },
  });

export const requestClearBookingList = () =>
  createAction({
    name: BOOKING_LIST_ACTIONS.CLEAR,
    type: BOOKING_LIST_ACTIONS.CLEAR,
  });

export const requestUpdateBookings = (bookings: IBookingUpdate[]) =>
  createRequestAction({
    url: CORE_SERVICE_URLS.BOOKING_UPDATE,
    method: 'POST',
    name: REQUEST_ACTIONS.BOOKING_UPDATE,
    data: bookings,
  });

export const requestUpdateSingleBooking = (booking: IBookingUpdate) =>
  createRequestAction({
    url: CORE_SERVICE_URLS.BOOKING_UPDATE_SINGLE,
    method: 'POST',
    name: REQUEST_ACTIONS.BOOKING_UPDATE_SINGLE,
    data: booking,
  });

export const requestCreateSingleBooking = (booking: IBookingCreateRequest) =>
  createRequestAction({
    url: createQueriedURL(CORE_SERVICE_URLS.CREATE_SINGLE_BOOKING, {
      ...booking,
      orderlineId: booking.orderlineId && booking.orderlineId < 0 ? null : booking.orderlineId,
      bookingOrigin: 'NEO-PRO',
    } as IBookingCreateRequest),
    method: 'POST',
    name: REQUEST_ACTIONS.BOOKING_CREATE_SINGLE,
  });

export const requestUploadImage = (name: string, imageData: string) =>
  createRequestAction({
    url: CORE_SERVICE_URLS.UPLOAD_IMAGE,
    method: 'POST',
    data: {
      name,
      imageData,
    },
    name: REQUEST_ACTIONS.UPLOAD_IMAGE,
  });

export const requestLoadProductsForEvent = (eventId: string) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.EVENT_PRODUCTS, { eventId }),
    method: 'GET',
    name: REQUEST_ACTIONS.EVENT_PRODUCTS,
  });

export const requestCreateOrderlineAction = (request: ICreateOrderline, customerid: number) =>
  createRequestAction<IOrderline>({
    url: routeReplacer(CORE_SERVICE_URLS.CREATE_ORDERLINE, { customerid }),
    method: 'POST',
    name: REQUEST_ACTIONS.CREATE_ORDERLINE,
    data: {
      ...request,
    },
  });

export const requestCreatePaymentAction = (request: ICreatePaymentRequest, orderlineId: number) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.CREATE_PAYMENT_FOR_ORDERLINE, { orderlineId }),
    method: 'POST',
    name: REQUEST_ACTIONS.CREATE_PAYMENT_FOR_ORDERLINE,
    data: {
      ...request,
    },
  });

export const requestActiveProductsForCustomer = (providerId: string, customerId: number) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.CUSTOMER_ACTIVE_ORDERLINES, { providerId, customerId }),
    method: 'GET',
    name: REQUEST_ACTIONS.CUSTOMER_ACTIVE_ORDERLINES,
  });

export const requestArchivedProductsForCustomer = (providerId: string, customerId: number) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.CUSTOMER_ARCHIVED_ORDERLINES, { providerId, customerId }),
    method: 'GET',
    name: REQUEST_ACTIONS.CUSTOMER_ARCHIVED_ORDERLINES,
  });

export const requestEventProductsForCustomer = (
  customerId: string,
  eventId: string,
  onlyUsable = true,
) =>
  createRequestAction({
    url: createQueriedURL(
      routeReplacer(CORE_SERVICE_URLS.CUSTOMER_EVENT_ORDERLINES, { customerId, eventId }),
      { onlyUsable },
    ),
    method: 'GET',
    name: REQUEST_ACTIONS.CUSTOMER_EVENT_ORDERLINES,
  });

export const requestPaymentMethods = (domain: string) =>
  createRequestAction({
    url: routeReplacer(CORE_SERVICE_URLS.GET_PAYMENTMETHODS, { domain }),
    method: 'GET',
    name: REQUEST_ACTIONS.PAYMENT_METHODS,
  });

export const requestDownloadJobs = (domain: string) =>
  createRequestAction({
    url: createQueriedURL(routeReplacer(CORE_SERVICE_URLS.DOWNLOAD_JOBS, { domain }), {
      filterTypes: ['DownloadJobCustomers'],
      includeFailed: true,
      onlyMy: true,
    }),
    name: REQUEST_ACTIONS.JOB_STATUS,
  });

const requestActiveBookingsForCustomer = (customerId: number, domain: string) =>
  createRequestAction({
    url: createQueriedURL(routeReplacer(CORE_SERVICE_URLS.FULL_BOOKING_LIST, { domain }), {
      customerId,
      bookingStatus: BookingStatus.BOOKED,
      begin: new Date().toISOString(),
    }),
    name: REQUEST_ACTIONS.ACTIVE_CUSTOMER_BOOKINGS,
  });

const requestCancelledBookingsForCustomer = (customerId: number, domain: string) =>
  createRequestAction({
    url: createQueriedURL(routeReplacer(CORE_SERVICE_URLS.FULL_BOOKING_LIST, { domain }), {
      customerId,
      bookingStatus: BookingStatus.CANCELLED,
    }),
    name: REQUEST_ACTIONS.CANCELLED_CUSTOMER_BOOKINGS,
  });

const requestPastBookingsForCustomer = (customerId: number, domain: string) =>
  createRequestAction({
    url: createQueriedURL(routeReplacer(CORE_SERVICE_URLS.FULL_BOOKING_LIST, { domain }), {
      customerId,
      end: new Date().toISOString(),
    }),
    name: REQUEST_ACTIONS.PAST_CUSTOMER_BOOKINGS,
  });

const customerBookingActions = {
  [CustomerBookingType.ACTIVE]: requestActiveBookingsForCustomer,
  [CustomerBookingType.CANCELLED]: requestCancelledBookingsForCustomer,
  [CustomerBookingType.PAST]: requestPastBookingsForCustomer,
};

export const requestBookingForType = (
  type: CustomerBookingType,
  customerId: number,
  domain: string,
) => () => customerBookingActions[type](customerId, domain);

export const requestCustomerTags = (customerIds: number[], providerUUID: string) =>
  createRequestAction({
    url: createQueriedURL(CORE_SERVICE_URLS.CUSTOMER_TAGS, {
      customerIds,
      end: providerUUID,
    }),
    name: REQUEST_ACTIONS.CUSTOMER_TAGS,
  });
